// 261023 E3-P3h2 (H6) — lớp phủ «? Hướng dẫn»: vòng khoanh vùng thật (`[data-tour=…]`) + thẻ «Mục i/10» với Trước / Tiếp / Thoát. Logic chọn mục/điều hướng ở `tourView.ts` (hàm thuần, có jest).
// Trạng thái ở query `tour=n` (mở thẳng được); KHÔNG tự bật. Mục trỏ vùng chưa có trên màn ⇒ thẻ ở giữa kèm câu «vùng này hiện khi…», không lỗi.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TourStep } from './tourView';
import { TOUR_LENGTH, tourKeyAction } from './tourView';

interface Props {
  step: TourStep;
  onPrev: () => void;
  onNext: () => void;
  onExit: () => void;
}

const PAD = 6;
const findTargets = (target: string): HTMLElement[] => Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`)).filter((e) => e.getClientRects().length > 0);

const WorkbenchTour: React.FC<Props> = ({ step, onPrev, onNext, onExit }) => {
  const [ring, setRing] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [card, setCard] = useState<{ left: number; top: number; width: number }>({ left: 12, top: 100, width: 380 });
  const [missing, setMissing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const els = findTargets(step.target);
    const cw = Math.min(400, window.innerWidth - 24);
    const ch = cardRef.current?.offsetHeight ?? 220;
    if (!els.length) { setMissing(true); setRing(null); setCard({ left: Math.max(12, (window.innerWidth - cw) / 2), top: Math.max(12, window.innerHeight * 0.3), width: cw }); return; }
    setMissing(false);
    const r0 = els[0].getBoundingClientRect();
    if (r0.top < 60 || r0.bottom > window.innerHeight - 60) els[0].scrollIntoView({ block: 'center' });
    let x1 = 1e9; let y1 = 1e9; let x2 = -1; let y2 = -1;
    els.forEach((e) => { const r = e.getBoundingClientRect(); x1 = Math.min(x1, r.left); y1 = Math.min(y1, r.top); x2 = Math.max(x2, r.right); y2 = Math.max(y2, r.bottom); });
    y1 = Math.max(y1, 0); y2 = Math.min(y2, window.innerHeight);
    setRing({ x: x1 - PAD, y: y1 - PAD, w: x2 - x1 + 2 * PAD, h: y2 - y1 + 2 * PAD });
    const left = Math.max(12, Math.min(x1, window.innerWidth - cw - 12));
    let top: number;
    if (y2 + PAD + 12 + ch < window.innerHeight - 8) top = y2 + PAD + 12;
    else if (y1 - PAD - 12 - ch > 8) top = y1 - PAD - 12 - ch;
    else top = Math.max(8, Math.min(100, window.innerHeight - ch - 8));
    setCard({ left, top, width: cw });
  }, [step.target]);

  // Màn vừa điều hướng ⇒ vùng đích render muộn: thử lại vài lần trong ~1,2 giây rồi mới coi là «chưa có».
  useLayoutEffect(() => {
    place();
    const timers = [80, 250, 600, 1200].map((ms) => window.setTimeout(place, ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [place, step.n]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const a = tourKeyAction(e.key, e.target as HTMLElement | null);   // R4/F5: không cướp mũi tên khi đang gõ
      if (a === 'exit') onExit(); else if (a === 'next') onNext(); else if (a === 'prev') onPrev();
    };
    window.addEventListener('resize', place);
    window.addEventListener('keydown', onKey);
    document.querySelector('.wb .wb-scroll')?.addEventListener('scroll', place);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('keydown', onKey); document.querySelector('.wb .wb-scroll')?.removeEventListener('scroll', place); };
  }, [place, onExit, onNext, onPrev]);

  const last = step.n === TOUR_LENGTH;
  return createPortal(
    <>
      <div className="wb-tour-block" aria-hidden="true" onClick={(e) => e.stopPropagation()} />
      {ring && <div className="wb-tour-ring" aria-hidden="true" style={{ left: ring.x, top: ring.y, width: ring.w, height: ring.h }} />}
      <div ref={cardRef} className="wb-tour-card" role="dialog" aria-label={`Hướng dẫn, mục ${step.n} trên ${TOUR_LENGTH}`} style={{ left: card.left, top: card.top, width: card.width }}>
        <div className="wb-tour-n">Mục {step.n}/{TOUR_LENGTH}</div>
        <b className="wb-tour-t">{step.title}</b>
        <ul className="wb-tour-ul">{step.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
        {missing && <p className="wb-tour-missing" role="note">{step.missing}</p>}
        {step.detail && <details><summary>Xem chi tiết</summary><p>{step.detail}</p></details>}
        <div className="wb-tour-b">
          <button type="button" className="wb-tour-btn wb-tour-txt" onClick={onExit}>Thoát</button>
          <span className="wb-tour-sp" />
          <button type="button" className="wb-tour-btn" onClick={onPrev} disabled={step.n === 1}>Trước</button>
          <button type="button" className="wb-tour-btn wb-tour-pri" onClick={last ? onExit : onNext}>{last ? 'Xong' : 'Tiếp'}</button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default WorkbenchTour;
